import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import api from '../../api';
import './ReservationListPage.css';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ReservationListPage = () => {
    const { serverId } = useParams();
    const navigate     = useNavigate();
    const { success, error: notifError } = useNotification();
    const { t } = useLanguage();

    const [sessions,      setSessions]      = useState([]);
    const [liveSessionId, setLiveSessionId] = useState('');
    const [enabled,       setEnabled]       = useState(true);
    const [togglingFeature, setTogglingFeature] = useState(false);
    const [loading,       setLoading]       = useState(true);

    // Création inline
    const [showCreate, setShowCreate] = useState(false);
    const [newTitle,   setNewTitle]   = useState('');
    const [creating,   setCreating]   = useState(false);

    // Recherche
    const [searchTerm, setSearchTerm] = useState('');

    // Renommage inline : { id: sessionId, value: string }
    const [renaming, setRenaming] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [sessRes, configRes] = await Promise.all([
                api.get(`/reservation-sessions/${serverId}`).catch(() => ({ data: [] })),
                api.get(`/reservation-config/${serverId}`).catch(() => ({ data: {} })),
            ]);
            const list = Array.isArray(sessRes.data) ? sessRes.data : [];
            // Tri : plus récent en premier
            list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setSessions(list);
            setLiveSessionId(configRes.data?.activeSessionId || '');
            setEnabled(configRes.data?.enabled !== false);
        } catch {
            notifError(t('resaList.loadError', '❌ Impossible de charger les fiches'));
        } finally {
            setLoading(false);
        }
    }, [serverId, notifError, t]);

    useEffect(() => { load(); }, [load]);

    const toggleFeature = async () => {
        const next = !enabled;
        setTogglingFeature(true);
        try {
            await api.patch(`/reservation-config/${serverId}/enabled`, { enabled: next });
            setEnabled(next);
            success(next ? t('resaList.enabledToast', '✅ Réservation activée pour les membres') : t('resaList.disabledToast', 'Réservation désactivée pour les membres'));
        } catch {
            notifError(t('resaList.toggleError', '❌ Impossible de modifier l\'activation'));
        } finally {
            setTogglingFeature(false);
        }
    };

    const handleCreate = async () => {
        if (!newTitle.trim()) return;
        setCreating(true);
        try {
            const res = await api.post('/reservation-sessions', { serverId, title: newTitle.trim() });
            success(t('resaList.created', '✅ Fiche créée !'));
            navigate(`/server/${serverId}/reservation/${res.data._id}/edit`);
        } catch {
            notifError(t('resaList.createError', '❌ Erreur lors de la création'));
            setCreating(false);
        }
    };

    const startRename = (session) => setRenaming({ id: session._id, value: session.title });

    const commitRename = async () => {
        if (!renaming) return;
        const trimmed = renaming.value.trim();
        if (!trimmed) { setRenaming(null); return; }
        const original = sessions.find(s => s._id === renaming.id)?.title;
        if (trimmed === original) { setRenaming(null); return; }
        try {
            await api.put(`/reservation-sessions/${renaming.id}`, { title: trimmed });
            setSessions(prev => prev.map(s => s._id === renaming.id ? { ...s, title: trimmed } : s));
            success(t('resaList.renamed', '✅ Fiche renommée'));
        } catch {
            notifError(t('resaList.renameError', '❌ Erreur lors du renommage'));
        } finally {
            setRenaming(null);
        }
    };

    const handleDelete = async (sessionId, title) => {
        if (!window.confirm(t('resaList.confirmDelete', 'Supprimer « {title} » ? Cette action est irréversible.', { title }))) return;
        try {
            await api.delete(`/reservation-sessions/${sessionId}`);
            setSessions(prev => prev.filter(s => s._id !== sessionId));
            if (liveSessionId === sessionId) setLiveSessionId('');
            success(t('resaList.deleted', '🗑️ Fiche supprimée'));
        } catch {
            notifError(t('resaList.deleteError', '❌ Erreur lors de la suppression'));
        }
    };

    const handleClone = async (session) => {
        const defaultTitle = `${session.title} ${t('resaList.copySuffix', '(copie)')}`;
        const chosenTitle = window.prompt(t('resaList.copyName', 'Nom de la copie :'), defaultTitle);
        if (chosenTitle === null) return;

        const title = chosenTitle.trim();
        if (!title) {
            notifError(t('resaList.copyNameEmpty', '❌ Le nom de la copie ne peut pas être vide'));
            return;
        }

        try {
            const res = await api.post(`/reservation-sessions/${serverId}/${session._id}/clone`, { title });
            const copiedCount = Number(res.data?.copiedCount || 0);
            success(t('resaList.copied', '✅ Fiche copiée ({n} ligne(s))', { n: copiedCount }));
            navigate(`/server/${serverId}/reservation/${res.data?.session?._id}/edit`);
        } catch {
            notifError(t('resaList.copyError', '❌ Erreur lors de la copie de la fiche'));
        }
    };

    const renderStatus = (session) => {
        if (session._id === liveSessionId)
            return <span className="c2-badge c2-badge--green"><span className="c2-diamond" />{t('resaList.statusLive', 'En cours')}</span>;
        if (session.swapWindowOpen)
            return <span className="c2-badge c2-badge--purple"><span className="c2-diamond c2-diamond--purple" />{t('resaList.statusSwap', 'Échanges ouverts')}</span>;
        if (session.frozen)
            return <span className="c2-badge"><span className="c2-diamond c2-diamond--outline" />{t('resaList.statusFrozen', 'Figée')}</span>;
        return <span className="c2-badge c2-muted"><span className="c2-diamond c2-diamond--outline" />{t('resaList.statusClosed', 'Fermée')}</span>;
    };

    const filtered = sessions.filter(s =>
        s.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const liveCount = sessions.filter(s => s._id === liveSessionId).length;
    const swapCount = sessions.filter(s => s.swapWindowOpen).length;

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="clipboard"
                title={t('navigation.reservation', 'Réservation')}
                subtitle={t('resaList.subtitle', 'Créez et gérez vos fiches de réservation')}
                actions={
                    <>
                        <button type="button" className="c2-icon-btn" onClick={load} title={t('common.refresh', 'Actualiser')} aria-label={t('common.refresh', 'Actualiser')}>
                            <Icon name="refresh" size={18} />
                        </button>
                        <button type="button" className="c2-btn c2-btn--primary" onClick={() => setShowCreate(v => !v)}>
                            <Icon name="plus" size={18} />
                            {t('resaList.newSheet', 'Nouvelle fiche')}
                        </button>
                    </>
                }
            />

            {/* ── Activation de la fonctionnalité ── */}
            <section className={`c2-card resa-feature${enabled ? ' is-on' : ''}`}>
                <div className="resa-feature__text">
                    <h2 className="c2-section__title">
                        <Icon name="user" size={20} />
                        {enabled ? t('resaList.featureOn', 'Réservation activée pour les membres') : t('resaList.featureOff', 'Réservation désactivée pour les membres')}
                    </h2>
                    <p className="c2-section__desc">
                        {enabled
                            ? t('resaList.featureOnDesc', 'Les membres voient les onglets « Mes préférences », « Fiche de réservation » et « Enchères » dans leur espace.')
                            : t('resaList.featureOffDesc', 'Les onglets de réservation sont masqués dans l\'espace membre. Vous pouvez quand même préparer vos fiches ici.')}
                    </p>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={t('resaList.toggleLabel', 'Activer la réservation pour les membres')}
                    className={`c2-switch c2-switch--lg${enabled ? ' is-on' : ''}`}
                    onClick={toggleFeature}
                    disabled={loading || togglingFeature}
                >
                    <span className="c2-switch__knob" />
                </button>
            </section>

            <div className="resa-meta">
                <span className="c2-chip">{sessions.length !== 1 ? t('resaList.sheetsCount', '{n} fiches', { n: sessions.length }) : t('resaList.sheetCount', '{n} fiche', { n: sessions.length })}</span>
                {liveCount > 0 && <span className="c2-chip c2-chip--green">{t('resaList.liveCount', '{n} en cours', { n: liveCount })}</span>}
                {swapCount > 0 && <span className="c2-chip c2-chip--purple">{swapCount !== 1 ? t('resaList.swapsOpen', '{n} échanges ouverts', { n: swapCount }) : t('resaList.swapOpen', '{n} échange ouvert', { n: swapCount })}</span>}
            </div>

            {/* ── Création ── */}
            {showCreate && (
                <section className="c2-card resa-create">
                    <label className="c2-field resa-create__field">
                        <span className="c2-field__label">{t('resaList.sheetName', 'Nom de la fiche')}</span>
                        <input
                            className="c2-input"
                            type="text"
                            placeholder={t('resaList.sheetNamePlaceholder', 'Ex : Fiche S7 — Semaine 1')}
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleCreate()}
                            autoFocus
                        />
                    </label>
                    <button type="button" className="c2-btn c2-btn--primary" onClick={handleCreate} disabled={creating || !newTitle.trim()}>
                        {creating ? t('resaList.creating', 'Création...') : t('resaList.create', 'Créer')}
                    </button>
                    <button type="button" className="c2-btn c2-btn--ghost" onClick={() => { setShowCreate(false); setNewTitle(''); }}>
                        {t('common.cancel', 'Annuler')}
                    </button>
                </section>
            )}

            {/* ── Liste des fiches ── */}
            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="clipboard" size={20} />{t('resaList.sheets', 'Fiches')}</h2>
                    <input
                        type="search"
                        className="c2-input c2-input--sm resa-search"
                        placeholder={t('resaList.searchPlaceholder', 'Rechercher une fiche...')}
                        aria-label={t('resaList.searchLabel', 'Rechercher une fiche')}
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading ? (
                    <div className="c2-loading"><div className="c2-spinner" /></div>
                ) : filtered.length === 0 ? (
                    <div className="c2-empty">
                        <Icon name="clipboard" size={36} />
                        <p>{sessions.length === 0 ? t('resaList.noSheet', 'Aucune fiche créée') : t('resaList.noMatch', 'Aucune fiche ne correspond à votre recherche.')}</p>
                        {sessions.length === 0 && (
                            <button type="button" className="c2-btn c2-btn--primary" onClick={() => setShowCreate(true)}>
                                <Icon name="plus" size={18} />
                                {t('resaList.createFirst', 'Créer la première fiche')}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="c2-table-wrap">
                        <table className="c2-table">
                            <thead>
                                <tr>
                                    <th>{t('resaList.sheet', 'Fiche')}</th>
                                    <th>{t('resaList.status', 'Statut')}</th>
                                    <th>{t('resaList.createdOn', 'Créée le')}</th>
                                    <th><span className="visually-hidden">{t('common.actions', 'Actions')}</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((session) => (
                                    <tr key={session._id} className="resa-row">
                                        <td>
                                            {renaming?.id === session._id ? (
                                                <input
                                                    className="c2-input c2-input--sm"
                                                    value={renaming.value}
                                                    onChange={e => setRenaming(r => ({ ...r, value: e.target.value }))}
                                                    onBlur={commitRename}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') commitRename();
                                                        if (e.key === 'Escape') setRenaming(null);
                                                    }}
                                                    aria-label={t('resaList.newName', 'Nouveau nom de la fiche')}
                                                    autoFocus
                                                />
                                            ) : (
                                                <div className="resa-title">
                                                    <button
                                                        type="button"
                                                        className="resa-title__open"
                                                        onClick={() => navigate(`/server/${serverId}/reservation/${session._id}/edit`)}
                                                    >
                                                        {session.title}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="c2-icon-btn c2-icon-btn--sm resa-title__rename"
                                                        onClick={() => startRename(session)}
                                                        title={t('resaList.rename', 'Renommer')}
                                                        aria-label={t('resaList.renameItem', 'Renommer {title}', { title: session.title })}
                                                    >
                                                        <Icon name="pencil" size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                        <td>{renderStatus(session)}</td>
                                        <td className="c2-muted">{formatDate(session.createdAt)}</td>
                                        <td className="is-num">
                                            <div className="resa-actions">
                                                <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => navigate(`/server/${serverId}/reservation/${session._id}/edit`)}>
                                                    {t('resaList.open', 'Ouvrir')}
                                                </button>
                                                <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => handleClone(session)} title={t('resaList.copySheet', 'Copier la fiche')} aria-label={t('resaList.copyItem', 'Copier {title}', { title: session.title })}>
                                                    <Icon name="copy" size={16} />
                                                </button>
                                                <button type="button" className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger" onClick={() => handleDelete(session._id, session.title)} title={t('resaList.deleteSheet', 'Supprimer la fiche')} aria-label={t('resaList.deleteItem', 'Supprimer {title}', { title: session.title })}>
                                                    <Icon name="trash" size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default ReservationListPage;
